<template>
    <RedesignBaseChart
        :option="chartOption"
        :widget="data"
        :hide="!hasData"
        classes="h-96"
    >
        <template #pre>
            <div
                v-if="!hasData"
                class="flex flex-col items-center w-full h-full justify-center"
            >
                <RedesignWidgetNoData :widget="data" />
            </div>
        </template>
    </RedesignBaseChart>
</template>

<script lang="ts" setup>
//TODO now this dataType doesnt exist check the component when appear
import { computed } from 'vue'
import { ComposeOption } from 'echarts/core'
import { FunnelSeriesOption } from 'echarts/charts'
import {
    LegendComponentOption,
    TitleComponentOption,
    TooltipComponentOption,
} from 'echarts/components'

import { WidgetExtended } from '@/types/dashboard'
import RedesignBaseChart from '@/components/RedesignComponents/Charts/RedesignBaseChart.vue'
import RedesignWidgetNoData from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetNoData.vue'
import useWidgetDataWithRefreshInterval from '@/composables/useWidgetDataWithRefreshInterval'

/* Types */
interface FunnelDataItem {
    name: string
    y: number
    color?: string
}

type ECOption = ComposeOption<
    | FunnelSeriesOption
    | TitleComponentOption
    | TooltipComponentOption
    | LegendComponentOption
>

/* Props */
const props = withDefaults(
    defineProps<{
        data: WidgetExtended
        editable?: boolean
    }>(),
    {
        editable: false
    }
)

/* Composable */
const { data: loadedData } = useWidgetDataWithRefreshInterval<FunnelDataItem[][], { data: WidgetExtended }, 'data'>(props, 'data')

/* Computed */
const seriesData = computed(() => {
    if (!loadedData.value?.length) {
        return []
    }

    // The API returns an array of series, each with a data array
    // For Highcharts funnel3d, the data format was: series: [{ data: [{name, y, color}] }]
    // We need to transform this into ECharts funnel format
    const firstSeries = loadedData.value[0]

    if (!firstSeries) {
        return []
    }

    // Handle both array of items and array of series objects
    if (Array.isArray(firstSeries)) {
        return firstSeries.map((item: FunnelDataItem) => ({
            name: item.name,
            value: item.y,
            itemStyle: item.color ? { color: item.color } : undefined
        }))
    }

    // If it's a series object with data property
    const seriesObj = firstSeries as unknown as { data?: FunnelDataItem[] }
    if (seriesObj.data && Array.isArray(seriesObj.data)) {
        return seriesObj.data.map((item: FunnelDataItem) => ({
            name: item.name,
            value: item.y,
            itemStyle: item.color ? { color: item.color } : undefined
        }))
    }

    return []
})

const hasData = computed(() => {
    return seriesData.value.length > 0
})

const chartOption = computed<ECOption>(() => {
    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'item',
            formatter: '{b}: {c}'
        },
        legend: {
            show: false
        },
        series: [
            {
                name: 'Funnel',
                type: 'funnel',
                left: '10%',
                top: 20,
                bottom: 20,
                width: '80%',
                minSize: '10%',
                maxSize: '100%',
                sort: 'descending',
                gap: 2,
                label: {
                    show: true,
                    position: 'inside',
                    formatter: '{b} ({c})',
                    fontSize: 12
                },
                labelLine: {
                    length: 10,
                    lineStyle: {
                        width: 1,
                        type: 'solid'
                    }
                },
                itemStyle: {
                    borderColor: '#fff',
                    borderWidth: 1
                },
                emphasis: {
                    label: {
                        fontSize: 14
                    }
                },
                data: seriesData.value
            }
        ]
    }
})

</script>
